import { type ReactNode, useCallback } from "react";

import { useHistory, useRouteMatch } from "react-router-dom";
import { useTracking } from "react-tracking";

import {
  BUI_LINK_CLASSES,
  BuiLink,
  type IBuiLinkProps,
} from "@btnet/blueprint-components/BuiLink";
import { cn } from "@btnet/utilities-core";

import {
  BTAnalyticsEvent,
  type IBTTrackingData,
  trackBTEvent,
} from "../analytics/analytics";
import { Hotkey } from "../Hotkey/Hotkey";
import type {
  IHotkeyWithPopoverConfig,
  THotkeyCommands,
} from "../Hotkey/hotkey.types";
import { getHotkeyProps } from "../Hotkey/hotkey.utility";
import "./BTLink.less";
import { useBTLinkRouting } from "./BTLinkRoutingContext";
import type { IBTLinkRouting } from "./link.types";
import { isRouteLocationState, scrollToElementById } from "./link.utility";

/**
 * Public props for {@link BTLink} / {@link BTLinkRelative}. This is the exact
 * signature the ~301 existing consumers rely on — do not change it. App-specific
 * routing is supplied separately via {@link IBTLinkInjectedProps} or the ambient
 * `BTLinkRoutingProvider`, not by consumers.
 *
 * If using linkType 'scroll', make sure that your href is the same as the id of
 * the element you want to scroll to.
 */
export interface IBTLinkProps
  extends Omit<IBuiLinkProps, "target" | "rel" | "to" | "underline"> {
  /**
   * Route/location to navigate to. Optional: external links (rendered as a plain
   * `<a>`) use `href` instead and can omit `to` entirely, and **action links**
   * — where `onClick` does the work and there is no destination — omit both.
   *
   * An action link still renders `href=""` so it keeps the implicit `link` role
   * and its accessible name, and BTLink calls `preventDefault` on the click for
   * you (an empty href resolves to the current url, which would otherwise reload
   * the page). Pass an explicit `href` to opt out of that handling.
   */
  to?: IBuiLinkProps["to"];
  children?: ReactNode;
  shouldOpenNewTab?: boolean;
  "data-testid"?: string;
  linkType?: "link" | "list" | "menuList" | "scroll" | "tel" | "sms" | "email";
  hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;

  /**
   * When true, the link text is underlined. Defaults to true.
   */
  isUnderline?: boolean;

  /**
   * Automatically handles links to SPA pages and adds /app in front of the
   * route if needed.
   */
  useAutoSPARouting?: boolean;
}

/**
 * Public props for {@link BTLinkRelative}. Identical to {@link IBTLinkProps}
 * except `to` is **required**: BTLinkRelative resolves the target against the
 * current route match (`${match.url}${to}`), so an omitted `to` would produce a
 * broken route string. Unlike {@link BTLink}, it has no external-link (`href` /
 * tel / sms) mode that would justify omitting `to`.
 */
export interface IBTLinkRelativeProps extends Omit<IBTLinkProps, "to"> {
  to: IBuiLinkProps["to"];
}

/**
 * Routing as accepted on {@link BTLink} props. Optional: when omitted, the link
 * reads it from the ambient `BTLinkRoutingProvider`. An explicit prop always
 * wins over the provider's value. If neither a prop nor a provider supplies
 * `routing`, BTLink throws (routing is required to render).
 *
 * Analytics is not injected — BTLink self-reports link clicks through BDS
 * tracking (`trackBTEvent`).
 */
export interface IBTLinkInjectedProps {
  /** Routing resolution; falls back to `BTLinkRoutingProvider` when omitted. */
  routing?: IBTLinkRouting;
}

/**
 * Resolves the routing BTLink needs from either an explicit prop or the ambient
 * {@link BTLinkRoutingProvider}. An explicit prop always wins; when neither a
 * prop nor a provider supplies `routing`, this throws (routing is required to
 * render).
 */
function useResolvedRouting(
  routingProp: IBTLinkRouting | undefined,
): IBTLinkRouting {
  const ambient = useBTLinkRouting();
  const routing = routingProp ?? ambient;
  if (!routing) {
    throw new Error(
      "BTLink requires routing: pass a `routing` prop or render within a <BTLinkRoutingProvider>.",
    );
  }
  return routing;
}

/**
 * The renderer for {@link BTLink} / {@link BTLinkRelative}. Delegates router
 * links to BuiLink (no direct react-router-dom `Link`), renders plain anchors
 * for tel/sms/email and empty-`to` links, and composes the in-core Hotkey.
 */
function BTLinkInternal({
  children,
  className = "",
  hotkey,
  href,
  isUnderline = true,
  linkType,
  onClick,
  routing,
  shouldOpenNewTab = false,
  // Consumed by the outer BTLink; stripped here so it isn't spread onto the DOM.
  useAutoSPARouting: _useAutoSPARouting,
  ...restProps
}: IBTLinkProps & { routing: IBTLinkRouting }) {
  const history = useHistory();
  // Self-report link clicks through BDS tracking, matching BdsLink/BTButton, so
  // consumers don't wire analytics themselves. The hook return value satisfies
  // react-tracking's TrackingProp, and dispatches through the ambient tracking
  // context (a no-op when none is present).
  const tracking = useTracking<IBTTrackingData>();
  const testid = restProps["data-testid"];

  // An "action link" has no destination at all: no `to` and no `href`, because
  // the behavior lives in `onClick` (or in a parent that injects one, e.g. a
  // dropdown trigger). Rendering it as a bare <a> with no `href` would drop the
  // implicit `link` role and with it the accessible name, so it still gets
  // `href=""`, which keeps it exposed to assistive tech and keyboard-focusable.
  // `href=""` resolves to the CURRENT url, so the default action has to be
  // suppressed or the browser reloads the page the user is already on. Keyed on
  // `href === undefined` so a caller that passes an explicit `href=""` and
  // handles its own `preventDefault` is left alone.
  const isActionLink =
    href === undefined && (!restProps.to || restProps.to.toString() === "");
  const resolvedHref = isActionLink ? "" : href;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (isActionLink) {
        event.preventDefault();
      }
      if (onClick) {
        onClick(event);
      }
      if (linkType === "scroll") {
        scrollToElementById(href!);
      }

      trackBTEvent(tracking, {
        element: "Link",
        event: BTAnalyticsEvent.LinkClick,
        testid,
      });
    },
    [href, isActionLink, linkType, onClick, tracking, testid],
  );

  let linkListStyling = "";
  switch (linkType) {
    case "link":
      break;

    case "list":
      linkListStyling = "linkListItem";
      break;

    case "menuList":
      linkListStyling = "menuListItem";
      break;
  }
  // list/menuList render gray, not link-blue. BuiLink applies its blue color via
  // a `utilities`-layer Tailwind utility that beats the `app`-layer `.less` gray,
  // so re-apply the gray as a utility here (cn/tailwind-merge lets it win).
  const listColorUtility =
    linkType === "list" || linkType === "menuList" ? "text-fg-default" : "";
  const classNameWithDefault = cn(
    className,
    "BTLink",
    linkListStyling,
    listColorUtility,
  );

  let modifiedTarget: "_blank" | undefined;
  let modifiedRel: "noopener noreferrer" | undefined;

  if (shouldOpenNewTab) {
    modifiedTarget = "_blank";
    modifiedRel = "noopener noreferrer";
  }

  // Plain <a> paths (tel/sms and empty-`to` external links) don't render through
  // BuiLink, so they'd miss its typography/color styling. Reuse BuiLink's shared
  // base classes so anchors stay in sync with router links.
  const buiLinkClasses = cn(
    BUI_LINK_CLASSES,
    { underline: isUnderline },
    classNameWithDefault,
  );

  if (linkType === "tel" || linkType === "sms") {
    const { to: _to, ...propsWithoutTo } = restProps;
    return (
      <a className={buiLinkClasses} href={href} {...propsWithoutTo}>
        {children}
      </a>
    );
  }

  const link =
    !restProps.to || restProps.to.toString() === "" ? (
      <a
        {...restProps}
        className={buiLinkClasses}
        target={modifiedTarget}
        onClick={handleClick}
        rel={modifiedRel}
        href={resolvedHref}
      >
        {children}
      </a>
    ) : (
      <BuiLink
        {...restProps}
        to={restProps.to}
        underline={isUnderline}
        className={classNameWithDefault}
        target={modifiedTarget}
        onClick={handleClick}
        rel={modifiedRel}
        href={href}
      >
        {children}
      </BuiLink>
    );

  if (hotkey) {
    return (
      <Hotkey
        {...getHotkeyProps(hotkey)}
        onCommand={() => {
          if (onClick) {
            onClick({} as React.MouseEvent<HTMLAnchorElement, MouseEvent>);
          }
          if (shouldOpenNewTab) {
            window.open(
              routing.formatRouteForNewTab(restProps.to?.toString() ?? ""),
              "_blank",
              "noopener noreferrer",
            );
          } else if (restProps.to) {
            history.push(restProps.to.toString());
          } else if (href) {
            window.location.assign(href);
          }
        }}
      >
        {link}
      </Hotkey>
    );
  }
  return link;
}

/**
 * Bui-styled link with SPA-aware routing, analytics, hotkey, and link-type
 * behavior. Rebuilt on BuiLink and relocated to core; the host app supplies
 * routing resolution via {@link IBTLinkInjectedProps} (a `routing` prop or the
 * ambient `BTLinkRoutingProvider`) so core stays free of any static import of
 * the app's routing subsystem. Link-click analytics is self-reported through
 * BDS tracking, so consumers don't wire it up.
 */
export function BTLink({
  to,
  href,
  useAutoSPARouting,
  routing: routingProp,
  ...otherProps
}: IBTLinkProps & IBTLinkInjectedProps) {
  const routing = useResolvedRouting(routingProp);

  // temporary until all warnings are gone - just to be safe
  let toValue = to;
  if (
    !isRouteLocationState(toValue) &&
    toValue?.toString().toLowerCase().startsWith("/app")
  ) {
    toValue = toValue.toString().replace("/app", "");
    console.warn(
      "Do not pass /app as part of your route. Pass the part after /app",
    );
  }

  /**
   * Update 'to' field to dynamically add base route automatically.
   * Remove once we are fully SPA.
   */
  const updateTo = () => {
    const isSPA = routing.isSpa;
    if (isRouteLocationState(to) && isSPA) {
      return to;
    }
    if (!useAutoSPARouting && !isRouteLocationState(to)) {
      return toValue ?? "";
    }

    if (isSPA) {
      return toValue?.toString() ?? "";
    } else {
      return "";
    }
  };

  /**
   * Redirect 'to' fields to use href links when we are not in SPA.
   * Remove once we are fully SPA.
   */
  const updateHref = () => {
    const isSPA = routing.isSpa;
    if (isRouteLocationState(to) && !isSPA) {
      return routing.appBase + to.pathname;
    }
    if (!useAutoSPARouting) {
      return href;
    }

    if (isSPA) {
      // A SPA link routes through `to`, so its href is unused — except when
      // there is no destination at all. Returning "" there would hand
      // BTLinkInternal a defined href and hide the action-link case from it, so
      // pass the caller's `href` through untouched (as the non-SPA branch does).
      return toValue?.toString() ? "" : href;
    } else {
      const toString = toValue?.toString() ?? "";
      if (toString === "") {
        return href;
      } else {
        if (toString.includes(".aspx")) {
          console.warn(
            "Do not pass webform urls through BTLink's to prop, use BTExternalLink or href instead",
          );
          return toString;
        } else {
          return `${routing.appBase}${toString}`;
        }
      }
    }
  };

  return (
    <BTLinkInternal
      to={updateTo()}
      href={updateHref()}
      routing={routing}
      {...otherProps}
    />
  );
}

/**
 * Use this link whenever navigating to a route nested within your page.
 * @param props Whatever props you would normally pass to BTLink.
 */
export function BTLinkRelative({
  to,
  routing: routingProp,
  ...rest
}: IBTLinkRelativeProps & IBTLinkInjectedProps) {
  const match = useRouteMatch();
  const routing = useResolvedRouting(routingProp);

  if (typeof to === "object") {
    return (
      <BTLinkInternal
        {...rest}
        routing={routing}
        to={{ ...to, pathname: `${match.url}${to.pathname}` }}
      />
    );
  }
  return (
    <BTLinkInternal {...rest} routing={routing} to={`${match.url}${to}`} />
  );
}
